// AlphaListView.jsx

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// Context
import { useNodeTypes } from 'context/NodeTypesContext';

// Utilities
import { getSingularLabel } from 'utilities/Languages';
import { buildDetachEdgeLabel } from 'utilities/ViewRows';

// Services
import { deleteEdge } from 'services/nodes/NodeEdgeService';

// Hooks
import useDefaultNodeOpen from 'views/hooks/useDefaultNodeOpen';
import useListNodelistHydration from 'views/list/hooks/useListNodelistHydration';

// CSS
import './AlphaListView.css';

// Components
import ListViewItem from 'views/list/item/ListViewItem';
import { buildListViewItemData, resolveListViewItemConfig } from 'views/list/item/listViewItemData';
import IconButton from 'components/iconbutton/IconButton';
import { toast } from 'react-toastify';
import { writeNodeDragData } from 'views/dragDrop/nodeDragData';
import {
  ALPHA_LIST_LETTERS,
  getAlphaListScrollParent,
  groupNodesByAlphaLetter,
} from './alphaListGrouping';

/**
 * AlphaListView
 *
 * List items grouped by first letter, with an A–Z jump rail.
 *
 * @returns {ReactNode} Alphabetized list view.
 */
const AlphaListView = React.memo(({
  view,
  typeDef,
  nodes = [],
  parentNodeKey = null,
  parentNodeTypeLabel = '',
  language = 'en',
  onItemClick = null,
  maxNodes = null,
  enforceExplicitProperties = false,
  readOnly = false,
  nodeDragEnabled = false,
  showIndex = true,
}) => {
  const handleOpenNode = useDefaultNodeOpen();
  const { getType } = useNodeTypes();
  const usesPerNodeTypeConfig = Boolean(view?.recordSearch);
  const sectionRefs = useRef({});
  const [activeLetter, setActiveLetter] = useState('');

  const listItemConfig = useMemo(() => (
    usesPerNodeTypeConfig
      ? null
      : resolveListViewItemConfig({
        enforceExplicitProperties,
        typeDef,
        view,
      })
  ), [enforceExplicitProperties, typeDef, usesPerNodeTypeConfig, view]);

  const recordSearchView = useMemo(() => (
    usesPerNodeTypeConfig
      ? {
        ...view,
        catchAll: true,
        type: 'ALPHA_LIST',
      }
      : view
  ), [usesPerNodeTypeConfig, view]);

  const baseDisplayedNodes = useMemo(() => {
    if (!Array.isArray(nodes)) return [];
    if (!Number.isFinite(maxNodes) || maxNodes <= 0) return nodes;
    return nodes.slice(0, maxNodes);
  }, [nodes, maxNodes]);

  const { groups, letters } = useMemo(
    () => groupNodesByAlphaLetter(baseDisplayedNodes),
    [baseDisplayedNodes],
  );

  const groupedNodes = useMemo(
    () => groups.flatMap((group) => group.nodes),
    [groups],
  );

  const getItemConfig = useCallback((node) => {
    if (usesPerNodeTypeConfig) {
      return resolveListViewItemConfig({
        enforceExplicitProperties: false,
        typeDef: getType(node?.type),
        view: recordSearchView,
      });
    }
    return listItemConfig;
  }, [getType, listItemConfig, recordSearchView, usesPerNodeTypeConfig]);

  const { resolveNodelistTitle } = useListNodelistHydration({
    nodes: groupedNodes,
    getItemConfig,
  });

  const handleNodeClick = useCallback((event, node) => {
    event?.preventDefault?.();
    if (typeof onItemClick === 'function') {
      event?.stopPropagation?.();
      onItemClick(node);
      return;
    }
    handleOpenNode(node);
  }, [handleOpenNode, onItemClick]);

  const handleNodeDoubleClick = useCallback((event, node) => {
    if (readOnly) return;
    event?.preventDefault?.();
    if (typeof onItemClick === 'function') {
      event?.stopPropagation?.();
      return;
    }
    handleOpenNode(node);
  }, [handleOpenNode, onItemClick, readOnly]);

  const handleDetachNode = useCallback(async (node) => {
    const parentKey = String(parentNodeKey || '').trim();
    const childKey = String(node?.key || '').trim();
    if (!parentKey || !childKey) return;

    try {
      await deleteEdge(parentKey, childKey);
    } catch (error) {
      toast.error(`Could not remove connection: ${error?.message || 'Unknown error'}`);
    }
  }, [parentNodeKey]);

  const getDetachActionLabel = useCallback((node) => {
    const nodeTypeKey = String(node?.type || typeDef?.key || '').trim();
    const activeTypeKey = String(typeDef?.key || '').trim();
    const useActiveTypeDef = nodeTypeKey
      && activeTypeKey
      && nodeTypeKey.toUpperCase() === activeTypeKey.toUpperCase();
    const childNodeTypeLabel = useActiveTypeDef
      ? getSingularLabel(typeDef, nodeTypeKey, language)
      : nodeTypeKey;

    return buildDetachEdgeLabel({
      childNodeTypeLabel,
      parentNodeTypeLabel,
    });
  }, [language, parentNodeTypeLabel, typeDef]);

  const renderTopRightAction = useCallback((node) => {
    if (readOnly) return null;
    const childKey = String(node?.key || '').trim();
    if (!childKey) return null;

    const parentKey = String(parentNodeKey || '').trim();
    if (!parentKey) return null;

    return (
      <IconButton
        iconKey="do_not_disturb_on"
        iconSize={20}
        title={getDetachActionLabel(node)}
        onClick={(event) => {
          event?.preventDefault?.();
          event?.stopPropagation?.();
          handleDetachNode(node);
        }}
      />
    );
  }, [getDetachActionLabel, handleDetachNode, parentNodeKey, readOnly]);

  const renderItem = useCallback((node) => {
    const nodeTypeDef = usesPerNodeTypeConfig ? getType(node?.type) : typeDef;
    const itemConfig = usesPerNodeTypeConfig
      ? resolveListViewItemConfig({
        enforceExplicitProperties: false,
        typeDef: nodeTypeDef,
        view: recordSearchView,
      })
      : listItemConfig;
    const itemData = buildListViewItemData({
      config: itemConfig,
      language,
      node,
      resolveNodelistTitle,
    });

    return (
      <ListViewItem
        key={node?.key}
        node={node}
        coverUrl={itemData.coverUrl}
        subtitle={itemData.subtitle}
        enumPills={itemData.enumPills}
        fieldValues={itemData.fieldValues}
        topRightAction={renderTopRightAction(node)}
        draggable={nodeDragEnabled && Boolean(node?.key)}
        onDragStart={(event) => {
          writeNodeDragData(event, node);
        }}
        onClick={(event) => handleNodeClick(event, node)}
        onDoubleClick={(event) => handleNodeDoubleClick(event, node)}
      />
    );
  }, [
    getType,
    handleNodeClick,
    handleNodeDoubleClick,
    language,
    listItemConfig,
    nodeDragEnabled,
    recordSearchView,
    renderTopRightAction,
    resolveNodelistTitle,
    typeDef,
    usesPerNodeTypeConfig,
  ]);

  useEffect(() => {
    if (!groups.length) {
      setActiveLetter('');
      return undefined;
    }

    const firstLetter = groups[0]?.letter || '';
    setActiveLetter((previous) => previous || firstLetter);

    const headers = groups
      .map((group) => sectionRefs.current[group.letter])
      .filter(Boolean);
    if (!headers.length) return undefined;

    const root = getAlphaListScrollParent(headers[0]);
    const observer = new IntersectionObserver((entries) => {
      const visible = entries
        .filter((entry) => entry.isIntersecting)
        .sort((left, right) => left.boundingClientRect.top - right.boundingClientRect.top);
      const nextLetter = visible[0]?.target?.dataset?.letter;
      if (nextLetter) setActiveLetter(nextLetter);
    }, {
      root,
      rootMargin: '-8% 0px -70% 0px',
      threshold: 0,
    });

    headers.forEach((header) => observer.observe(header));
    return () => observer.disconnect();
  }, [groups]);

  const handleJumpToLetter = useCallback((letter) => {
    if (!letters.has(letter)) return;
    const header = sectionRefs.current[letter];
    if (!header) return;
    header.scrollIntoView({
      block: 'start',
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ? 'auto'
        : 'smooth',
    });
    setActiveLetter(letter);
  }, [letters]);

  return (
    <div className="alpha-list-view">
      <div className="alpha-list-groups">
        {groups.map((group) => (
          <section
            key={group.letter}
            className="alpha-list-group"
            aria-labelledby={`alpha-list-letter-${group.letter === '#' ? 'other' : group.letter}`}
          >
            <div
              id={`alpha-list-letter-${group.letter === '#' ? 'other' : group.letter}`}
              className="alpha-list-letter"
              data-letter={group.letter}
              ref={(element) => {
                sectionRefs.current[group.letter] = element;
              }}
            >
              {group.letter}
            </div>
            <div className="alpha-list-items">
              {group.nodes.map(renderItem)}
            </div>
          </section>
        ))}
      </div>

      {showIndex ? (
        <nav className="alpha-list-index" aria-label="Jump to letter">
          {ALPHA_LIST_LETTERS.map((letter) => {
            const isAvailable = letters.has(letter);
            return (
              <button
                key={letter}
                type="button"
                className={[
                  'alpha-list-index-letter',
                  isAvailable && activeLetter === letter ? 'is-active' : '',
                  isAvailable ? '' : 'is-disabled',
                ].filter(Boolean).join(' ')}
                disabled={!isAvailable}
                aria-label={isAvailable ? `Jump to ${letter}` : `${letter} unavailable`}
                onClick={() => handleJumpToLetter(letter)}
              >
                {letter}
              </button>
            );
          })}
        </nav>
      ) : null}
    </div>
  );
});

AlphaListView.displayName = 'AlphaListView';

export default AlphaListView;
